import { type ReactNode, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, FileText, Loader2 } from "lucide-react";
import { apiError, recruiterSelfApi } from "../api";
import { useToast } from "@/app/providers/Toaster";

/**
 * Antes de "Mi trabajo": aceptar los Términos y Condiciones para Colaboradores
 * (y otra vez si cambia la versión). Si la consulta falla, no bloquea: se
 * vuelve a pedir en la próxima carga.
 */
export function TermsCheck({ children }: { children: ReactNode }) {
  const { push } = useToast();
  const qc = useQueryClient();
  const [checked, setChecked] = useState(false);
  const q = useQuery({ queryKey: ["recruiter-self", "terms"], queryFn: recruiterSelfApi.termsStatus, retry: 1 });
  const pending = (q.data?.documents ?? []).filter((d) => !d.accepted);
  const mut = useMutation({
    mutationFn: () => recruiterSelfApi.acceptTerms(pending.map((d) => d.key)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recruiter-self", "terms"] });
      push({ kind: "success", message: "Gracias. Ya puedes usar tu espacio." });
    },
    onError: (err) => push({ kind: "error", message: apiError(err, "No pudimos guardar tu aceptación.") }),
  });

  if (q.isLoading) return null;
  if (q.isError || pending.length === 0) return <>{children}</>;

  const update = pending.some((d) => d.previouslyAccepted);
  return (
    <div className="max-w-xl mx-auto nx-card p-6 space-y-4">
      <p className="text-xs text-fg-muted uppercase tracking-wider font-semibold">Mixcore · Go Up Cloud SpA</p>
      <h1 className="nx-h1 text-xl">{update ? "Actualizamos los Términos y Condiciones" : "Antes de empezar, revisa los Términos y Condiciones"}</h1>
      <p className="text-sm text-fg-muted">
        Regulan el uso de tu espacio de colaborador. Tu relación con Mixcore sigue rigiéndose por el Acuerdo de Reclutador
        Comercial que firmaste.
      </p>
      <ul className="space-y-2">
        {pending.map((d) => (
          <li key={d.key} className="flex items-center gap-2 rounded-md border border-border p-3 text-sm">
            <FileText size={16} className="text-fg-subtle shrink-0" />
            <a href={d.url} target="_blank" rel="noreferrer" className="text-accent font-medium inline-flex items-center gap-1">
              {d.title} <span className="text-fg-subtle font-normal">v{d.version}</span> <ExternalLink size={13} />
            </a>
          </li>
        ))}
        {q.data?.privacyUrl && (
          <li className="flex items-center gap-2 rounded-md border border-border p-3 text-sm">
            <FileText size={16} className="text-fg-subtle shrink-0" />
            <a href={q.data.privacyUrl} target="_blank" rel="noreferrer" className="text-accent font-medium inline-flex items-center gap-1">
              Política de Privacidad <ExternalLink size={13} />
            </a>
          </li>
        )}
      </ul>
      <label className="flex items-start gap-2 text-sm text-fg">
        <input type="checkbox" className="mt-1" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
        He leído y acepto los Términos y Condiciones para Colaboradores y la Política de Privacidad.
      </label>
      <button type="button" className="nx-btn-primary w-full" disabled={!checked || mut.isPending} onClick={() => mut.mutate()}>
        {mut.isPending && <Loader2 size={14} className="animate-spin" />}
        Aceptar y continuar
      </button>
    </div>
  );
}
